import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import * as JspUtil from '../common/JspUtil';
import { useJspVariables, useDebugRender, usePageTitle } from '../hooks';
import { IceIcon, PageContainer } from '../components';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
const cellStyle = { borderTop: '0px', fontSize: '0.6em', padding: '5px' };

const PageSamlMessage = () => {
    useDebugRender('PageSamlMessage');
    usePageTitle('SAML Authentication');
    const location = useLocation();
    const { launcher, panelStyle } = useJspVariables();

    const [pageMessage] = useState(() => {
        return location?.state?.pageMessage || '';
    });
    const [title, setTitle] = useState('SAML Authentication');
    const [samlMessage, setSamlMessage] = useState('');
    const [samlAttrs, setSamlAttrs] = useState(null);

    useEffect(() => {
        if (JspUtil.isValueAvailable(window.jspSamlMessage)) {
            setSamlMessage(window.jspSamlMessage);
        }
        if (JspUtil.isValueAvailable(window.jspSamlAttrs)) {
            const attrs = JSON.parse(window.jspSamlAttrs);
            setSamlAttrs(attrs);
            setTitle('Federation Configuration');
        }
    }, []);

    const onContinueClick = () => {
        if (JspUtil.isValueAvailable(window.jspSamlLoginUrl)) {
            window.location.href = window.jspSamlLoginUrl;
        }
    };

    const showSamlLogin = JspUtil.isValueAvailable(window.jspSamlLoginUrl);
    const showButton = !pageMessage;

    return (
        <PageContainer id="pageSamlMessage" dataAutomationId="page_saml_message">
            <Box style={panelStyle}>
                <IceIcon launcher={launcher} />
                <Box>
                    <Typography fontWeight={500}>{title}</Typography>
                    {samlMessage && <Typography variant="body2">{samlMessage}</Typography>}
                    {pageMessage && <Typography variant="body2">{pageMessage}</Typography>}
                </Box>
                {showSamlLogin && showButton &&
                    <Button variant="contained" fullWidth
                        onClick={onContinueClick}
                        data-automation-id="saml_message_continue_btn">
                        {JspUtil.getLabelFromJspVariables('action_go_to_login')}
                    </Button>
                }
                {samlAttrs && samlAttrs.certs && samlAttrs.certs.length > 0 &&
                    <Box>
                        <Typography fontWeight={500}>{JspUtil.getLabelFromJspVariables('note_saml_cert')}</Typography>
                        <Table>
                            <TableBody>
                                {samlAttrs.certs.map((row, i) => {
                                    if (row) {
                                        return (
                                            <TableRow key={i}>
                                                <TableCell sx={{ borderTop: '0px' }}>
                                                    <Table>
                                                        <TableBody>
                                                            <TableRow>
                                                                <TableCell sx={cellStyle}>{JspUtil.getLabelFromJspVariables('label_cert_subject')}</TableCell>
                                                                <TableCell sx={cellStyle}>{row.subjectdn}</TableCell>
                                                            </TableRow>
                                                            <TableRow>
                                                                <TableCell sx={cellStyle}>{JspUtil.getLabelFromJspVariables('label_cert_issuer')}</TableCell>
                                                                <TableCell sx={cellStyle}>{row.issuerdn}</TableCell>
                                                            </TableRow>
                                                            <TableRow>
                                                                <TableCell sx={cellStyle}>{JspUtil.getLabelFromJspVariables('label_cert_not_before')}</TableCell>
                                                                <TableCell sx={cellStyle}>{row.notbefore}</TableCell>
                                                            </TableRow>
                                                            <TableRow>
                                                                <TableCell sx={cellStyle}>{JspUtil.getLabelFromJspVariables('label_cert_not_after')}</TableCell>
                                                                <TableCell sx={cellStyle}>{row.notafter}</TableCell>
                                                            </TableRow>
                                                            <TableRow>
                                                                <TableCell sx={cellStyle}>{JspUtil.getLabelFromJspVariables('label_cert_thumbprint')}</TableCell>
                                                                <TableCell sx={cellStyle}>{row.thumbprint}</TableCell>
                                                            </TableRow>
                                                        </TableBody>
                                                    </Table>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    }
                                    return null;
                                })}
                            </TableBody>
                        </Table>
                    </Box>
                }
                {samlAttrs && samlAttrs.attrs && samlAttrs.attrs.length > 0 &&
                    <Box>
                        <Typography fontWeight={500}>{JspUtil.getLabelFromJspVariables('label_configured_attributes')}</Typography>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell title="This is the attribute key configured via Self Service Application">{JspUtil.getLabelFromJspVariables('label_attr_key_configured')}</TableCell>
                                    <TableCell title="If this attribute is missing, please go to Self Service Application and update attribute settings">{JspUtil.getLabelFromJspVariables('label_attr_status')}</TableCell>
                                    <TableCell title="Extracted attribute value from Response">{JspUtil.getLabelFromJspVariables('label_value')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {samlAttrs.attrs.map((row, i) => {
                                    if (row) {
                                        return (
                                            <TableRow key={i} sx={{ backgroundColor: (row.status && row.status.startsWith('Missing')) ? '#f8d7da' : 'inherit' }}>
                                                <TableCell sx={cellStyle}>{row.key}</TableCell>
                                                <TableCell sx={cellStyle}>{row.status}</TableCell>
                                                <TableCell sx={cellStyle}>{row.value}</TableCell>
                                            </TableRow>
                                        );
                                    }
                                    return null;
                                })}
                            </TableBody>
                        </Table>
                        <Typography fontWeight={500}>{JspUtil.getLabelFromJspVariables('label_attrs_in_response')}</Typography>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell title="Attribute name in SAML Response">{JspUtil.getLabelFromJspVariables('label_attr_name')}</TableCell>
                                    <TableCell title="Attribute value in SAML Response">{JspUtil.getLabelFromJspVariables('label_attr_value')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {samlAttrs.saml.map((row, i) => {
                                    if (row) {
                                        return (
                                            <TableRow key={i}>
                                                <TableCell sx={cellStyle}>{row.key}</TableCell>
                                                <TableCell sx={cellStyle}>{row.value}</TableCell>
                                            </TableRow>
                                        );
                                    }
                                    return null;
                                })}
                            </TableBody>
                        </Table>
                    </Box>
                }
            </Box>
        </PageContainer>
    );
};

export default PageSamlMessage;
